// =============================================
// Admin Kindergarten Calendar — דריסות לוח שנה לגנים
// ניהול אירועים ברמת הגן (סגירות, אירועים מיוחדים)
// =============================================

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CalendarDays, Plus, X, ChevronLeft, ChevronRight,
  Edit3, Trash2, CheckCircle2, XCircle,
  AlertTriangle, Save, Search, Filter, ChevronDown, ChevronUp,
} from 'lucide-react';
import { adminApi } from '../../services/api';
import type {
  AdminKindergartenDto,
  KindergartenCalendarDto,
  UpsertKindergartenCalendarRequest,
  SystemCalendarDto,
} from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useAdminStore } from '../../stores/adminStore';
import { calendarColors as colors } from '../../styles/colors';
import {
  HEBREW_DAYS,
  HEBREW_MONTHS,
  formatDate,
  parseDate,
  daysInMonth,
  firstDayOfMonth,
} from '../../utils/hebrewCalendar';

// =============================================
// Constants
// =============================================

const KG_EVENT_TYPES = [
  { value: 1, label: 'סגור', color: '#ef4444', bgColor: '#fef2f2' },
  { value: 2, label: 'אירוע', color: '#f59e0b', bgColor: '#fef3c7' },
  { value: 3, label: 'אחר', color: '#8b5cf6', bgColor: '#f5f3ff' },
];

// =============================================
// Helpers
// =============================================

interface FormState {
  date: string;
  eventType: number;
  eventName: string;
  isKindergartenClosed: boolean;
  notes: string;
}

const emptyForm: FormState = {
  date: '',
  eventType: 1,
  eventName: '',
  isKindergartenClosed: true,
  notes: '',
};

function getKgEventType(value: number) {
  return KG_EVENT_TYPES.find(t => t.value === value) || KG_EVENT_TYPES[0];
}

function formatDisplayDate(dateStr: string): string {
  const { day, month } = parseDate(dateStr);
  return `${day} ${HEBREW_MONTHS[month]}`;
}

function isToday(year: number, month: number, day: number): boolean {
  const now = new Date();
  return now.getFullYear() === year && now.getMonth() === month && now.getDate() === day;
}

function isSaturday(year: number, month: number, day: number): boolean {
  return new Date(year, month, day).getDay() === 6;
}

// =============================================
// Props
// =============================================

interface Props {
  primaryColor: string;
  primaryLight: string;
}

// =============================================
// Component
// =============================================

export default function AdminKindergartenCalendar({ primaryColor, primaryLight }: Props) {
  const { user } = useAuthStore();
  const { selectedOrganizationId } = useAdminStore();
  const isSysAdmin = !user?.organizationPublicId;
  const orgFilter = user?.organizationPublicId || selectedOrganizationId || undefined;

  // State
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [kindergartens, setKindergartens] = useState<AdminKindergartenDto[]>([]);
  const [selectedKgs, setSelectedKgs] = useState<Set<string>>(new Set());
  const [kgCalendar, setKgCalendar] = useState<KindergartenCalendarDto[]>([]);
  const [systemCalendar, setSystemCalendar] = useState<SystemCalendarDto[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormState>({ ...emptyForm });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [cityFilter, setCityFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isKindergartensLoading, setIsKindergartensLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [kgSelectorOpen, setKgSelectorOpen] = useState(true);

  // Toasts
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), type === 'error' ? 4000 : 3000);
  };

  // Load kindergartens on mount / orgFilter change
  useEffect(() => {
    if (!orgFilter) {
      setKindergartens([]);
      setSelectedKgs(new Set());
      return;
    }
    const loadKindergartens = async () => {
      setIsKindergartensLoading(true);
      try {
        const result = await adminApi.getKindergartens(orgFilter);
        if (result.success && result.data) {
          setKindergartens(result.data);
        } else {
          setKindergartens([]);
        }
      } catch (err) {
        console.error('Failed to load kindergartens:', err);
        showToast('שגיאה בטעינת גנים', 'error');
      } finally {
        setIsKindergartensLoading(false);
      }
    };
    loadKindergartens();
  }, [orgFilter]);

  // Load calendar entries when year changes
  useEffect(() => {
    const loadCalendar = async () => {
      setIsLoading(true);
      try {
        const [kgResult, sysResult] = await Promise.all([
          adminApi.getKindergartenCalendar(year, orgFilter),
          adminApi.getSystemCalendar(year),
        ]);
        if (kgResult.success && kgResult.data) {
          setKgCalendar(kgResult.data);
        } else {
          setKgCalendar([]);
        }
        if (sysResult.success && sysResult.data) {
          setSystemCalendar(sysResult.data);
        } else {
          setSystemCalendar([]);
        }
      } catch (err) {
        console.error('Failed to load calendar:', err);
        showToast('שגיאה בטעינת לוח שנה', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    loadCalendar();
  }, [year, orgFilter]);

  // Unique cities from kindergartens
  const uniqueCities = useMemo(() => {
    const cities = new Set(kindergartens.map(k => k.city).filter(Boolean));
    return Array.from(cities).sort();
  }, [kindergartens]);

  // Filtered kindergartens
  const filteredKindergartens = useMemo(() => {
    return kindergartens.filter(k => {
      if (cityFilter && k.city !== cityFilter) return false;
      if (searchText && !k.name.includes(searchText) && !k.code.includes(searchText)) return false;
      return true;
    });
  }, [kindergartens, cityFilter, searchText]);

  // Filtered KG calendar entries (by selected KGs)
  const filteredKgCalendar = useMemo(() => {
    if (selectedKgs.size === 0) return [];
    return kgCalendar.filter(e => selectedKgs.has(e.kindergartenPublicId));
  }, [kgCalendar, selectedKgs]);

  // System calendar entries for a specific date
  const getSystemEntriesForDate = (dateStr: string): SystemCalendarDto[] => {
    return systemCalendar.filter(e => e.date === dateStr);
  };

  // KG calendar entries for a specific date (filtered by selected KGs)
  const getKgEntriesForDate = (dateStr: string): KindergartenCalendarDto[] => {
    return filteredKgCalendar.filter(e => e.date === dateStr);
  };

  // Month entries for list view
  const monthKgEntries = useMemo(() => {
    return filteredKgCalendar
      .filter(e => {
        const { month: m } = parseDate(e.date);
        return m === month;
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredKgCalendar, month]);

  // Group month entries by date
  const groupedMonthEntries = useMemo(() => {
    const groups: Record<string, KindergartenCalendarDto[]> = {};
    for (const entry of monthKgEntries) {
      if (!groups[entry.date]) groups[entry.date] = [];
      groups[entry.date].push(entry);
    }
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [monthKgEntries]);

  // Navigation
  const goToPreviousMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(y => y - 1);
    } else {
      setMonth(m => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(y => y + 1);
    } else {
      setMonth(m => m + 1);
    }
  };

  const goToToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth());
  };

  // KG selection
  const toggleKg = (publicId: string) => {
    setSelectedKgs(prev => {
      const next = new Set(prev);
      if (next.has(publicId)) {
        next.delete(publicId);
      } else {
        next.add(publicId);
      }
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedKgs(prev => {
      const next = new Set(prev);
      filteredKindergartens.forEach(k => next.add(k.publicId));
      return next;
    });
  };

  const clearAllSelected = () => {
    setSelectedKgs(new Set());
  };

  // Open modal for adding
  const openAddModal = (dateStr: string) => {
    if (selectedKgs.size === 0) {
      showToast('יש לבחור לפחות גן אחד', 'error');
      return;
    }
    setEditingId(null);
    setForm({
      date: dateStr,
      eventType: 1,
      eventName: '',
      isKindergartenClosed: true,
      notes: '',
    });
    setConfirmDelete(null);
    setShowModal(true);
  };

  // Open modal for editing
  const openEditModal = (entry: KindergartenCalendarDto) => {
    setEditingId(entry.id);
    setForm({
      date: entry.date,
      eventType: entry.eventType,
      eventName: entry.eventName || '',
      isKindergartenClosed: entry.isKindergartenClosed,
      notes: entry.notes || '',
    });
    setConfirmDelete(null);
    setShowModal(true);
  };

  // Handle day click
  const handleDayClick = (day: number) => {
    const dateStr = formatDate(year, month, day);
    const dayKgEntries = getKgEntriesForDate(dateStr);
    if (dayKgEntries.length === 1) {
      openEditModal(dayKgEntries[0]);
    } else {
      openAddModal(dateStr);
    }
  };

  // Save handler
  const handleSave = async () => {
    if (!form.eventName.trim()) {
      showToast('נא להזין שם אירוע', 'error');
      return;
    }
    if (selectedKgs.size === 0 && !editingId) {
      showToast('יש לבחור לפחות גן אחד', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        // Edit single entry: send with original KG
        const existingEntry = kgCalendar.find(e => e.id === editingId);
        if (!existingEntry) {
          showToast('שגיאה - רשומה לא נמצאה', 'error');
          setSaving(false);
          return;
        }
        const request: UpsertKindergartenCalendarRequest = {
          kindergartenPublicIds: [existingEntry.kindergartenPublicId],
          date: form.date,
          eventType: form.eventType,
          eventName: form.eventName.trim(),
          isKindergartenClosed: form.isKindergartenClosed,
          notes: form.notes.trim() || undefined,
        };
        const result = await adminApi.upsertKindergartenCalendar(request);
        if (result.success && result.data) {
          setKgCalendar(prev => {
            const updatedIds = new Set(result.data.map((e: KindergartenCalendarDto) => e.id));
            const filtered = prev.filter(e => e.id !== editingId && !updatedIds.has(e.id));
            return [...filtered, ...result.data];
          });
          showToast('רשומה עודכנה בהצלחה', 'success');
        } else {
          showToast('שגיאה בשמירה', 'error');
        }
      } else {
        // Create new entries for all selected KGs
        const request: UpsertKindergartenCalendarRequest = {
          kindergartenPublicIds: Array.from(selectedKgs),
          date: form.date,
          eventType: form.eventType,
          eventName: form.eventName.trim(),
          isKindergartenClosed: form.isKindergartenClosed,
          notes: form.notes.trim() || undefined,
        };
        const result = await adminApi.upsertKindergartenCalendar(request);
        if (result.success && result.data) {
          setKgCalendar(prev => {
            const newIds = new Set(result.data.map((e: KindergartenCalendarDto) => e.id));
            const filtered = prev.filter(e => !newIds.has(e.id));
            return [...filtered, ...result.data];
          });
          showToast('רשומות נוספו בהצלחה', 'success');
        } else {
          showToast('שגיאה בשמירה', 'error');
        }
      }

      setShowModal(false);
    } catch (err) {
      console.error('Failed to save KG calendar entry:', err);
      showToast('שגיאה בשמירה', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete handler
  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      const result = await adminApi.deleteKindergartenCalendarEntry(id);
      if (result.success) {
        setKgCalendar(prev => prev.filter(e => e.id !== id));
        showToast('רשומה נמחקה בהצלחה', 'success');
        setShowModal(false);
      } else {
        showToast('שגיאה במחיקה', 'error');
      }
    } catch (err) {
      console.error('Failed to delete KG calendar entry:', err);
      showToast('שגיאה במחיקה', 'error');
    } finally {
      setSaving(false);
      setConfirmDelete(null);
    }
  };

  // Calendar grid data
  const totalDays = daysInMonth(year, month);
  const startDay = firstDayOfMonth(year, month);

  // SysAdmin without org selected — show message
  if (isSysAdmin && !orgFilter) {
    return (
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          backgroundColor: colors.white, borderRadius: '16px',
          border: `1px solid ${colors.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
          <AlertTriangle size={40} color="#f59e0b" style={{ marginBottom: '16px' }} />
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: colors.text, margin: '0 0 8px 0' }}>
            יש לבחור ארגון
          </h2>
          <p style={{ fontSize: '14px', color: colors.textLight, margin: 0, maxWidth: '400px', marginInline: 'auto' }}>
            כדי לצפות ולערוך דריסות לגנים, יש לבחור ארגון מהתפריט הצדדי
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: colors.text, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CalendarDays size={24} color={primaryColor} />
            דריסות לוח שנה לגנים
          </h1>
          <p style={{ fontSize: '13px', color: colors.textLight, margin: '4px 0 0 0' }}>
            ניהול אירועים וסגירות ברמת הגן
            {' '}&middot; {filteredKgCalendar.length} רשומות
            {' '}&middot; נבחרו {selectedKgs.size} גנים
          </p>
        </div>
        <button
          onClick={() => {
            const todayStr = formatDate(now.getFullYear(), now.getMonth(), now.getDate());
            openAddModal(todayStr);
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '10px 18px', backgroundColor: primaryColor, color: 'white',
            border: 'none', borderRadius: '10px', cursor: 'pointer',
            fontSize: '13px', fontWeight: '600',
          }}
        >
          <Plus size={16} /> הוסף רשומה
        </button>
      </div>

      {/* Success / Error toasts */}
      <AnimatePresence>
        {toast && toast.type === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '12px 16px', backgroundColor: '#dcfce7',
              borderRadius: '10px', marginBottom: '16px', fontSize: '13px', color: '#166534',
            }}
          >
            <CheckCircle2 size={16} /> {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {toast && toast.type === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '12px 16px', backgroundColor: '#fef2f2',
              borderRadius: '10px', marginBottom: '16px', fontSize: '13px', color: '#991b1b',
            }}
          >
            <XCircle size={16} /> {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* KG Selector */}
      <div style={{
        backgroundColor: colors.white, borderRadius: '16px',
        border: `1px solid ${colors.border}`, padding: '16px 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '16px',
      }}>
        {/* Selector header */}
        <button
          onClick={() => setKgSelectorOpen(!kgSelectorOpen)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', border: 'none', background: 'none', cursor: 'pointer',
            padding: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={18} color={primaryColor} />
            <span style={{ fontSize: '15px', fontWeight: '700', color: colors.text }}>
              בחירת גנים
            </span>
            <span style={{ fontSize: '12px', fontWeight: '500', color: colors.textLight }}>
              (נבחרו {selectedKgs.size} גנים)
            </span>
          </div>
          {kgSelectorOpen ? (
            <ChevronUp size={18} color={colors.textLight} />
          ) : (
            <ChevronDown size={18} color={colors.textLight} />
          )}
        </button>

        {/* Selector body */}
        <AnimatePresence>
          {kgSelectorOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.2 }}
            >
              <div style={{ marginTop: '16px' }}>
                {/* Filter row */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {/* City filter */}
                  <div style={{ flex: '1', minWidth: '160px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: colors.textLight, marginBottom: '4px' }}>
                      סנן לפי עיר
                    </label>
                    <select
                      value={cityFilter}
                      onChange={e => setCityFilter(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 10px', border: `1px solid ${colors.border}`,
                        borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                        backgroundColor: colors.white, color: colors.text,
                      }}
                    >
                      <option value="">כל הערים</option>
                      {uniqueCities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  {/* Search */}
                  <div style={{ flex: '1', minWidth: '160px' }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: colors.textLight, marginBottom: '4px' }}>
                      חיפוש גן
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        placeholder="שם או קוד גן..."
                        style={{
                          width: '100%', padding: '8px 10px 8px 32px', border: `1px solid ${colors.border}`,
                          borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                        }}
                      />
                      <Search
                        size={14}
                        color={colors.textLight}
                        style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Select all / Clear all buttons */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <button
                    onClick={selectAllFiltered}
                    style={{
                      padding: '6px 14px', border: `1px solid ${primaryColor}`, borderRadius: '6px',
                      backgroundColor: primaryLight, cursor: 'pointer', fontSize: '12px',
                      fontWeight: '600', color: primaryColor,
                    }}
                  >
                    בחר הכל ({filteredKindergartens.length})
                  </button>
                  <button
                    onClick={clearAllSelected}
                    style={{
                      padding: '6px 14px', border: `1px solid ${colors.border}`, borderRadius: '6px',
                      backgroundColor: colors.white, cursor: 'pointer', fontSize: '12px',
                      fontWeight: '600', color: colors.textLight,
                    }}
                  >
                    נקה הכל
                  </button>
                </div>

                {/* KG checkbox list */}
                <div style={{
                  maxHeight: '200px', overflowY: 'auto',
                  border: `1px solid ${colors.border}`, borderRadius: '10px',
                  padding: '4px',
                }}>
                  {isKindergartensLoading ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: colors.textLight, fontSize: '13px' }}>
                      טוען גנים...
                    </div>
                  ) : filteredKindergartens.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: colors.textLight, fontSize: '13px' }}>
                      לא נמצאו גנים
                    </div>
                  ) : (
                    filteredKindergartens.map(kg => (
                      <label
                        key={kg.publicId}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '8px 10px', borderRadius: '8px', cursor: 'pointer',
                          backgroundColor: selectedKgs.has(kg.publicId) ? primaryLight : 'transparent',
                          transition: 'background-color 0.15s',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedKgs.has(kg.publicId)}
                          onChange={() => toggleKg(kg.publicId)}
                          style={{ accentColor: primaryColor, width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '13px', fontWeight: '500', color: colors.text, flex: 1 }}>
                          {kg.name}
                        </span>
                        <span style={{ fontSize: '11px', color: colors.textLight }}>
                          {kg.city}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Calendar card */}
      <div style={{
        backgroundColor: colors.white, borderRadius: '16px',
        border: `1px solid ${colors.border}`, padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '24px',
      }}>
        {/* Month/Year navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <button
            onClick={goToNextMonth}
            style={{
              padding: '8px', border: `1px solid ${colors.border}`, borderRadius: '8px',
              backgroundColor: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
            title="חודש הבא"
          >
            <ChevronRight size={18} color={colors.text} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={goToToday}
              style={{
                padding: '6px 12px', border: `1px solid ${primaryColor}`, borderRadius: '6px',
                backgroundColor: primaryLight, cursor: 'pointer', fontSize: '12px',
                fontWeight: '600', color: primaryColor,
              }}
            >
              היום
            </button>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: colors.text, margin: 0 }}>
              {HEBREW_MONTHS[month]} {year}
            </h2>
          </div>

          <button
            onClick={goToPreviousMonth}
            style={{
              padding: '8px', border: `1px solid ${colors.border}`, borderRadius: '8px',
              backgroundColor: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center',
            }}
            title="חודש קודם"
          >
            <ChevronLeft size={18} color={colors.text} />
          </button>
        </div>

        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
          {HEBREW_DAYS.map((dayName, i) => (
            <div
              key={dayName}
              style={{
                textAlign: 'center', padding: '8px 0', fontSize: '12px',
                fontWeight: '700', color: i === 6 ? '#ca8a04' : colors.textLight,
              }}
            >
              {dayName}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: colors.textLight }}>
            <div style={{
              width: '24px', height: '24px', border: `3px solid ${colors.border}`,
              borderTopColor: primaryColor, borderRadius: '50%',
              margin: '0 auto 8px', animation: 'spin 1s linear infinite',
            }} />
            <p style={{ fontSize: '13px' }}>טוען לוח שנה...</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {/* Empty cells before first day */}
            {Array.from({ length: startDay }).map((_, i) => (
              <div key={`empty-${i}`} style={{ minHeight: '90px' }} />
            ))}

            {/* Day cells */}
            {Array.from({ length: totalDays }).map((_, i) => {
              const day = i + 1;
              const dateStr = formatDate(year, month, day);
              const sysEntries = getSystemEntriesForDate(dateStr);
              const dayKgEntries = getKgEntriesForDate(dateStr);
              const today = isToday(year, month, day);
              const shabbat = isSaturday(year, month, day);

              return (
                <motion.div
                  key={day}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDayClick(day)}
                  style={{
                    minHeight: '90px',
                    padding: '6px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: today ? `2px solid ${primaryColor}` : `1px solid ${colors.border}`,
                    backgroundColor: today
                      ? '#ccfbf1'
                      : shabbat
                        ? '#fefce8'
                        : colors.white,
                    transition: 'background-color 0.15s',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  {/* Day number */}
                  <div style={{
                    fontSize: '13px', fontWeight: today ? '700' : '500',
                    color: today ? primaryColor : shabbat ? '#ca8a04' : colors.text,
                    marginBottom: '4px',
                  }}>
                    {day}
                  </div>

                  {/* System events (grayed out context) */}
                  {sysEntries.slice(0, 1).map(sysEntry => (
                    <div
                      key={`sys-${sysEntry.id}`}
                      style={{
                        fontSize: '8px',
                        fontWeight: '500',
                        fontStyle: 'italic',
                        padding: '1px 4px',
                        borderRadius: '3px',
                        backgroundColor: '#f3f4f6',
                        color: '#9ca3af',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: '13px',
                        marginBottom: '2px',
                      }}
                    >
                      {sysEntry.eventName}
                    </div>
                  ))}

                  {/* KG override entries (colored) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {dayKgEntries.slice(0, 2).map(entry => {
                      const evType = getKgEventType(entry.eventType);
                      return (
                        <div
                          key={entry.id}
                          onClick={e => { e.stopPropagation(); openEditModal(entry); }}
                          style={{
                            fontSize: '9px',
                            fontWeight: '600',
                            padding: '1px 4px',
                            borderRadius: '3px',
                            backgroundColor: evType.bgColor,
                            color: evType.color,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: '14px',
                            cursor: 'pointer',
                          }}
                        >
                          {entry.eventName || evType.label}
                        </div>
                      );
                    })}
                    {dayKgEntries.length > 2 && (
                      <div style={{ fontSize: '9px', color: colors.textLight, fontWeight: '500' }}>
                        +{dayKgEntries.length - 2} נוספים
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Events list for current month */}
      <div style={{
        backgroundColor: colors.white, borderRadius: '16px',
        border: `1px solid ${colors.border}`, padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        <h3 style={{
          fontSize: '16px', fontWeight: '700', color: colors.text,
          margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          <CalendarDays size={18} color={primaryColor} />
          אירועי גנים ב-{HEBREW_MONTHS[month]} {year}
          <span style={{ fontSize: '13px', fontWeight: '500', color: colors.textLight }}>
            ({monthKgEntries.length})
          </span>
        </h3>

        {selectedKgs.size === 0 ? (
          <div style={{
            textAlign: 'center', padding: '40px 24px',
            backgroundColor: colors.background, borderRadius: '12px',
          }}>
            <Filter size={36} color={colors.textLight} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <p style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: '0 0 4px 0' }}>
              לא נבחרו גנים
            </p>
            <p style={{ fontSize: '13px', color: colors.textLight, margin: 0 }}>
              בחר גנים מהרשימה למעלה כדי לראות אירועים
            </p>
          </div>
        ) : monthKgEntries.length === 0 && !isLoading ? (
          <div style={{
            textAlign: 'center', padding: '40px 24px',
            backgroundColor: colors.background, borderRadius: '12px',
          }}>
            <CalendarDays size={36} color={colors.textLight} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
            <p style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: '0 0 4px 0' }}>
              אין אירועי גנים בחודש זה
            </p>
            <p style={{ fontSize: '13px', color: colors.textLight, margin: 0 }}>
              לחץ על יום בלוח כדי להוסיף אירוע
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {groupedMonthEntries.map(([dateStr, dateEntries]) => (
              <div key={dateStr}>
                {/* Date group header */}
                <div style={{
                  fontSize: '12px', fontWeight: '700', color: colors.textLight,
                  marginBottom: '6px', paddingRight: '4px',
                }}>
                  {formatDisplayDate(dateStr)}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {dateEntries.map((entry, index) => {
                    const evType = getKgEventType(entry.eventType);
                    return (
                      <motion.div
                        key={entry.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px',
                          padding: '10px 14px', borderRadius: '10px',
                          border: `1px solid ${colors.border}`,
                          backgroundColor: colors.white,
                        }}
                      >
                        {/* Date badge */}
                        <div style={{
                          minWidth: '48px', textAlign: 'center',
                          padding: '4px 6px', borderRadius: '8px',
                          backgroundColor: colors.background,
                        }}>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: colors.text }}>
                            {parseDate(entry.date).day}
                          </div>
                          <div style={{ fontSize: '9px', color: colors.textLight, fontWeight: '500' }}>
                            {HEBREW_MONTHS[parseDate(entry.date).month].slice(0, 3)}
                          </div>
                        </div>

                        {/* Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                              {entry.eventName || evType.label}
                            </span>
                            {/* Event type badge */}
                            <span style={{
                              fontSize: '10px', fontWeight: '600', padding: '2px 8px',
                              borderRadius: '10px', backgroundColor: evType.bgColor, color: evType.color,
                            }}>
                              {evType.label}
                            </span>
                            {entry.isKindergartenClosed && (
                              <span style={{
                                fontSize: '10px', fontWeight: '600', padding: '2px 8px',
                                borderRadius: '10px', backgroundColor: '#fef2f2', color: '#ef4444',
                              }}>
                                סגור
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: colors.textLight, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: '500' }}>
                              {entry.kindergartenName}
                            </span>
                            {entry.notes && (
                              <span style={{
                                maxWidth: '180px', overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {entry.notes}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => openEditModal(entry)}
                            style={{
                              padding: '6px', border: `1px solid ${colors.border}`, borderRadius: '6px',
                              backgroundColor: 'transparent', cursor: 'pointer',
                              display: 'flex', alignItems: 'center',
                            }}
                            title="עריכה"
                          >
                            <Edit3 size={13} color={colors.textLight} />
                          </button>
                          <button
                            onClick={() => {
                              setConfirmDelete(entry.id);
                            }}
                            style={{
                              padding: '6px', border: '1px solid #fecaca', borderRadius: '6px',
                              backgroundColor: '#fef2f2', cursor: 'pointer',
                              display: 'flex', alignItems: 'center',
                            }}
                            title="מחק"
                          >
                            <Trash2 size={13} color="#ef4444" />
                          </button>
                        </div>

                        {/* Inline delete confirmation */}
                        {confirmDelete === entry.id && !showModal && (
                          <div style={{
                            position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '8px 12px', backgroundColor: '#fef2f2',
                            borderRadius: '8px', border: '1px solid #fecaca',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            zIndex: 10,
                          }}>
                            <span style={{ fontSize: '12px', color: '#991b1b', fontWeight: '500' }}>
                              למחוק?
                            </span>
                            <button
                              onClick={() => handleDelete(entry.id)}
                              style={{
                                padding: '4px 10px', border: 'none', borderRadius: '6px',
                                backgroundColor: '#ef4444', color: 'white', cursor: 'pointer',
                                fontSize: '11px', fontWeight: '600',
                              }}
                            >
                              אישור
                            </button>
                            <button
                              onClick={() => setConfirmDelete(null)}
                              style={{
                                padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: '6px',
                                backgroundColor: colors.white, cursor: 'pointer',
                                fontSize: '11px', color: colors.textLight,
                              }}
                            >
                              ביטול
                            </button>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 1000, padding: '16px',
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              style={{
                backgroundColor: colors.white, borderRadius: '16px',
                width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto',
                padding: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
              }}
              onMouseDown={e => e.stopPropagation()}
            >
              {/* Modal header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: colors.text, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarDays size={18} color={primaryColor} />
                  {editingId ? 'עריכת רשומה' : 'הוספת רשומה'}
                </h3>
                <button onClick={() => setShowModal(false)} style={{ padding: '4px', border: 'none', background: 'none', cursor: 'pointer' }}>
                  <X size={18} color={colors.textLight} />
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Date display (read-only) */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: colors.text, marginBottom: '6px' }}>
                    תאריך
                  </label>
                  <div style={{
                    padding: '10px 12px', backgroundColor: colors.background,
                    borderRadius: '8px', fontSize: '13px', color: colors.text,
                    border: `1px solid ${colors.border}`,
                  }}>
                    {form.date && formatDisplayDate(form.date)}
                  </div>
                </div>

                {/* Selected KGs info */}
                {!editingId && (
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: colors.text, marginBottom: '6px' }}>
                      גנים נבחרים
                    </label>
                    <div style={{
                      padding: '10px 12px', backgroundColor: primaryLight,
                      borderRadius: '8px', fontSize: '13px', color: primaryColor,
                      border: `1px solid ${primaryColor}20`,
                      fontWeight: '500',
                    }}>
                      {selectedKgs.size} גנים נבחרים
                    </div>
                  </div>
                )}

                {/* Editing: show which KG */}
                {editingId && (() => {
                  const editEntry = kgCalendar.find(e => e.id === editingId);
                  return editEntry ? (
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: colors.text, marginBottom: '6px' }}>
                        גן
                      </label>
                      <div style={{
                        padding: '10px 12px', backgroundColor: colors.background,
                        borderRadius: '8px', fontSize: '13px', color: colors.text,
                        border: `1px solid ${colors.border}`,
                      }}>
                        {editEntry.kindergartenName}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Event type select */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: colors.text, marginBottom: '6px' }}>
                    סוג אירוע *
                  </label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {KG_EVENT_TYPES.map(et => (
                      <button
                        key={et.value}
                        onClick={() => {
                          setForm(f => ({
                            ...f,
                            eventType: et.value,
                            isKindergartenClosed: et.value === 1 ? true : f.isKindergartenClosed,
                          }));
                        }}
                        style={{
                          padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
                          fontSize: '12px', fontWeight: '600',
                          border: form.eventType === et.value ? `2px solid ${et.color}` : `1px solid ${colors.border}`,
                          backgroundColor: form.eventType === et.value ? et.bgColor : colors.white,
                          color: form.eventType === et.value ? et.color : colors.textLight,
                          transition: 'all 0.15s',
                        }}
                      >
                        {et.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Event name */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: colors.text, marginBottom: '6px' }}>
                    שם אירוע *
                  </label>
                  <input
                    type="text"
                    value={form.eventName}
                    onChange={e => setForm(f => ({ ...f, eventName: e.target.value }))}
                    placeholder="לדוגמה: יום ספורט, שיפוץ, הפסקת חשמל..."
                    style={{
                      width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                      borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                </div>

                {/* IsKindergartenClosed checkbox */}
                <label style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                  border: `1px solid ${form.isKindergartenClosed ? '#fecaca' : colors.border}`,
                  backgroundColor: form.isKindergartenClosed ? '#fef2f2' : colors.white,
                  transition: 'all 0.15s',
                }}>
                  <input
                    type="checkbox"
                    checked={form.isKindergartenClosed}
                    onChange={e => setForm(f => ({ ...f, isKindergartenClosed: e.target.checked }))}
                    style={{ accentColor: '#ef4444', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <div>
                    <span style={{
                      fontSize: '13px', fontWeight: '600',
                      color: form.isKindergartenClosed ? '#ef4444' : colors.text,
                    }}>
                      הגן סגור
                    </span>
                    <p style={{ fontSize: '11px', color: colors.textLight, margin: '2px 0 0 0' }}>
                      סמן אם הגן לא פעיל ביום זה
                    </p>
                  </div>
                </label>

                {/* Notes */}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: colors.text, marginBottom: '6px' }}>
                    הערות
                  </label>
                  <textarea
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="הערות נוספות (אופציונלי)"
                    rows={3}
                    style={{
                      width: '100%', padding: '10px 12px', border: `1px solid ${colors.border}`,
                      borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
                      resize: 'vertical', fontFamily: 'inherit',
                    }}
                  />
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  {/* Save */}
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      padding: '12px', border: 'none', borderRadius: '10px',
                      backgroundColor: primaryColor, color: 'white', cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: '14px', fontWeight: '600', opacity: saving ? 0.7 : 1,
                      transition: 'opacity 0.15s',
                    }}
                  >
                    {saving ? (
                      <div style={{
                        width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: 'white', borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }} />
                    ) : (
                      <Save size={16} />
                    )}
                    {saving ? 'שומר...' : 'שמור'}
                  </button>

                  {/* Delete (only when editing) */}
                  {editingId && (
                    <>
                      {confirmDelete === editingId ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button
                            onClick={() => handleDelete(editingId)}
                            disabled={saving}
                            style={{
                              padding: '12px 16px', border: 'none', borderRadius: '10px',
                              backgroundColor: '#ef4444', color: 'white', cursor: saving ? 'not-allowed' : 'pointer',
                              fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px',
                            }}
                          >
                            <AlertTriangle size={14} /> אישור
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            style={{
                              padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '10px',
                              backgroundColor: 'transparent', cursor: 'pointer',
                              display: 'flex', alignItems: 'center',
                            }}
                          >
                            <X size={16} color={colors.textLight} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(editingId)}
                          style={{
                            padding: '12px 16px', border: '1px solid #fecaca', borderRadius: '10px',
                            backgroundColor: '#fef2f2', cursor: 'pointer',
                            fontSize: '13px', fontWeight: '600', color: '#ef4444',
                            display: 'flex', alignItems: 'center', gap: '6px',
                          }}
                        >
                          <Trash2 size={14} /> מחק
                        </button>
                      )}
                    </>
                  )}

                  {/* Cancel */}
                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      padding: '12px 16px', border: `1px solid ${colors.border}`, borderRadius: '10px',
                      backgroundColor: 'transparent', cursor: 'pointer',
                      fontSize: '13px', color: colors.textLight,
                    }}
                  >
                    ביטול
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Keyframes for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
